import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/roles/')({
  component: RolesListPage,
})

interface Role {
  id: string
  key: string
  name: string
  description: string | null
  isSystem: boolean
  userCount: number
}

function RolesListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  })

  const { data, isLoading } = useQuery(orpc.rbac.roles.list.queryOptions({ input: {} }))
  const roles = (data ?? []) as Role[]

  // Invalidate (not refetch) so the list is marked stale in the cache.
  // Clone navigates away immediately after — a component-local refetch()
  // loses its observer during the unmount, and the re-mounted list then
  // renders the pre-clone cache. invalidateQueries persists the "stale"
  // flag so the next mount refetches on its own.
  const invalidateList = () => qc.invalidateQueries({ queryKey: orpc.rbac.roles.list.key() })

  const deleteMutation = useMutation(
    orpc.rbac.roles.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Role deleted')
        invalidateList()
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const cloneMutation = useMutation(
    orpc.rbac.roles.clone.mutationOptions({
      onSuccess: (result) => {
        toast.success('Role cloned')
        invalidateList()
        navigate({
          to: '/settings/roles/$roleKey',
          params: { roleKey: (result as { id: string }).id },
        })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Roles & Permissions</h1>
          <p className="text-muted-foreground">
            Manage who can read, write, and administer data in each module.
          </p>
        </div>
        <Button asChild>
          <Link to="/settings/roles/new">
            <Plus className="h-4 w-4 mr-2" />
            Create role
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Users</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="max-w-md truncate text-muted-foreground">
                      {role.description ?? '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={role.isSystem ? 'bg-slate-100' : 'bg-blue-100'}
                      >
                        {role.isSystem ? 'System' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell>{role.userCount}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            cloneMutation.mutate({
                              id: role.id,
                              newKey: `${role.key}_copy`,
                              newName: `${role.name} (copy)`,
                            })
                          }
                          title="Clone"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        {!role.isSystem && (
                          <>
                            <Button size="sm" variant="ghost" asChild title="Edit">
                              <Link to="/settings/roles/$roleKey" params={{ roleKey: role.id }}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600"
                              onClick={() => setDeleteDialog({ open: true, id: role.id })}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role?</AlertDialogTitle>
            <AlertDialogDescription>
              Users currently assigned this role will lose its grants. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteDialog.id) deleteMutation.mutate({ id: deleteDialog.id })
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
